import React, { useState, useCallback } from 'react'
import moment from 'moment-timezone'
import {
    Button,
    Card,
    Input,
    TextArea,
    Icon,
    Heading,
    Paragraph,
    Checkbox
} from '@arnsbogroup/react-voicearchive'
import DateTime from 'react-datetime'
import 'react-datetime/css/react-datetime.css'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import Tabs from 'components/TabsRoutes'
import { FormattedDate, FormattedTime, FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'

const localizer = momentLocalizer(moment)
const DraggableCalendar = withDragAndDrop(Calendar)

const SchedulingPollForm = ({
    initialTitle = '',
    initialDescription = '',
    initialIncludeBranding = true,
    initialTimeSlots = [],
    basePath,
    onSubmit,
    isSubmitting = false,
    submitLabel,
    onCancel,
    showExistingIndicators = false,
}) => {
    const { formatMessage } = useIntl()

    const [title, setTitle] = useState(initialTitle)
    const [description, setDescription] = useState(initialDescription)
    const [includeBranding, setIncludeBranding] = useState(initialIncludeBranding)
    const [defaultDuration, setDefaultDuration] = useState(() => {
        if (initialTimeSlots.length > 0) {
            const first = initialTimeSlots[0]
            const diff = moment(first.endDatetime).diff(moment(first.startDatetime), 'minutes')
            return diff > 0 ? diff : 60
        }
        return 60
    })
    const [timeSlots, setTimeSlots] = useState(initialTimeSlots)

    const calendarEvents = timeSlots.map((slot, index) => ({
        id: index,
        title: `Slot ${index + 1}`,
        start: slot.startDatetime ? moment(slot.startDatetime).toDate() : new Date(),
        end: slot.endDatetime ? moment(slot.endDatetime).toDate() : new Date(),
        isExisting: slot.isExisting || false,
    }))

    const handleSelectSlot = useCallback((slotInfo) => {
        const startMoment = moment(slotInfo.start)
        const endMoment = moment(slotInfo.end)

        if (endMoment.diff(startMoment, 'minutes') === 15) {
            endMoment.add(defaultDuration - 15, 'minutes')
        }

        setTimeSlots(prev => [...prev, {
            uid: null,
            startDatetime: startMoment.format('YYYY-MM-DDTHH:mm'),
            endDatetime: endMoment.format('YYYY-MM-DDTHH:mm'),
            isExisting: false,
        }])

        toastr.success(formatMessage(messages.addedTimeSlotToast, {
            start: startMoment.format('MMM D, h:mm A'),
            end: endMoment.format('h:mm A'),
        }))
    }, [defaultDuration, formatMessage])

    const handleSelectEvent = useCallback((event) => {
        const slot = timeSlots[event.id]
        const confirmMsg = (showExistingIndicators && slot.isExisting)
            ? formatMessage(messages.removeExistingSlotWarning, {
                start: moment(event.start).format('MMM D, YYYY h:mm A'),
                end: moment(event.end).format('h:mm A'),
            })
            : formatMessage(messages.removeSlotConfirm, {
                start: moment(event.start).format('MMM D, YYYY h:mm A'),
                end: moment(event.end).format('h:mm A'),
            })

        if (window.confirm(confirmMsg)) {
            setTimeSlots(prev => prev.filter((_, i) => i !== event.id))
        }
    }, [formatMessage, showExistingIndicators, timeSlots])

    const handleEventDrop = useCallback(({ event, start, end }) => {
        setTimeSlots(prev => {
            const next = [...prev]
            next[event.id] = {
                ...next[event.id],
                startDatetime: moment(start).format('YYYY-MM-DDTHH:mm'),
                endDatetime: moment(end).format('YYYY-MM-DDTHH:mm'),
            }
            return next
        })
    }, [])

    const handleEventResize = useCallback(({ event, start, end }) => {
        setTimeSlots(prev => {
            const next = [...prev]
            next[event.id] = {
                ...next[event.id],
                startDatetime: moment(start).format('YYYY-MM-DDTHH:mm'),
                endDatetime: moment(end).format('YYYY-MM-DDTHH:mm'),
            }
            return next
        })
    }, [])

    const removeTimeSlot = (index) => {
        setTimeSlots(prev => prev.filter((_, i) => i !== index))
    }

    const handleTimeSlotChange = (index, field, value) => {
        setTimeSlots(prev => {
            const next = [...prev]
            next[index] = { ...next[index], [field]: value }
            return next
        })
    }

    const addTimeSlot = () => {
        setTimeSlots(prev => {
            const last = prev.at(-1)
            if (!last?.startDatetime || !last?.endDatetime) {
                return [...prev, { uid: null, startDatetime: '', endDatetime: '', isExisting: false }]
            }
            const lastStart = moment(last.startDatetime)
            const lastEnd = moment(last.endDatetime)
            const duration = lastEnd.diff(lastStart, 'minutes') || defaultDuration
            const newStart = lastEnd.clone()
            return [...prev, {
                uid: null,
                startDatetime: newStart.format('YYYY-MM-DDTHH:mm'),
                endDatetime: newStart.clone().add(duration, 'minutes').format('YYYY-MM-DDTHH:mm'),
                isExisting: false,
            }]
        })
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (!title.trim()) {
            toastr.error(formatMessage(messages.titleRequired))
            return
        }

        if (timeSlots.length === 0) {
            toastr.error(formatMessage(messages.atLeastOneSlot))
            return
        }

        for (let i = 0; i < timeSlots.length; i++) {
            if (!timeSlots[i].startDatetime || !timeSlots[i].endDatetime) {
                toastr.error(formatMessage(messages.slotTimesRequired, { slotNumber: i + 1 }))
                return
            }
            const start = moment(timeSlots[i].startDatetime)
            const end = moment(timeSlots[i].endDatetime)
            if (!start.isValid() || !end.isValid()) {
                toastr.error(formatMessage(messages.invalidDatetime, { slotNumber: i + 1 }))
                return
            }
            if (end.isSameOrBefore(start)) {
                toastr.error(formatMessage(messages.endAfterStart, { slotNumber: i + 1 }))
                return
            }
        }

        onSubmit({ title, description, includeBranding, timeSlots })
    }

    const hasExistingSlots = showExistingIndicators && timeSlots.some(s => s.isExisting)
    const defaultCalendarDate = timeSlots.length > 0
        ? moment(timeSlots[0].startDatetime).toDate()
        : new Date()

    return (
        <Card className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <Input
                        label={formatMessage(messages.title)}
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={formatMessage(messages.titlePlaceholder)}
                        required
                    />
                </div>

                <div>
                    <TextArea
                        label={formatMessage(messages.description)}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        placeholder={formatMessage(messages.descriptionPlaceholder)}
                    />
                </div>

                <div>
                    <Checkbox
                        checked={includeBranding}
                        onChange={(e) => setIncludeBranding(e.target.checked)}
                        label={formatMessage(messages.includeBranding)}
                    />
                </div>

                <div>
                    <Input
                        label={formatMessage(messages.defaultDuration)}
                        type="number"
                        value={defaultDuration}
                        onChange={(e) => setDefaultDuration(parseInt(e.target.value) || 60)}
                        min="5"
                        step="5"
                        required
                        description={formatMessage(messages.defaultDurationDescription)}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        <FormattedMessage {...messages.timeSlotOptions} values={{ count: timeSlots.length }} />
                    </label>

                    <Tabs
                        className="bg-white -mx-4 -mb-4"
                        childrenClassName="bg-white p-4"
                        tabsClassName="px-4 border-b border-gray-200"
                        tabs={[
                            {
                                message: formatMessage(messages.calendar),
                                to: `${basePath}/calendar`,
                                route: {
                                    path: `${basePath}/calendar`,
                                    exact: true,
                                    render: () => (
                                        <div>
                                            <div className="bg-primary border rounded-lg p-3 mb-3">
                                                <p className="text-sm text-white mb-0">
                                                    <FormattedMessage {...messages.calendarInstructions} values={{ duration: defaultDuration }} />
                                                </p>
                                            </div>
                                            <div style={{ height: '600px' }} className="bg-white rounded-lg border border-gray-300 p-2">
                                                <DraggableCalendar
                                                    localizer={localizer}
                                                    events={calendarEvents}
                                                    startAccessor="start"
                                                    endAccessor="end"
                                                    style={{ height: '100%' }}
                                                    selectable
                                                    onSelectSlot={handleSelectSlot}
                                                    onSelectEvent={handleSelectEvent}
                                                    onEventDrop={handleEventDrop}
                                                    onEventResize={handleEventResize}
                                                    resizable
                                                    defaultView="week"
                                                    views={['week', 'day', 'agenda']}
                                                    step={15}
                                                    timeslots={4}
                                                    defaultDate={defaultCalendarDate}
                                                    min={moment().set({ hour: 7, minute: 0 }).toDate()}
                                                    max={moment().set({ hour: 22, minute: 0 }).toDate()}
                                                    eventPropGetter={(event) => ({
                                                        style: {
                                                            backgroundColor: (hasExistingSlots && !event.isExisting) ? '#22c55e' : '#336295',
                                                            borderRadius: '4px',
                                                            opacity: 0.8,
                                                            color: 'white',
                                                            cursor: 'move',
                                                        }
                                                    })}
                                                />
                                            </div>
                                            {hasExistingSlots && (
                                                <div className="mt-2 flex gap-4 text-xs text-gray-600">
                                                    <span className="flex items-center gap-1">
                                                        <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#336295' }} />
                                                        <FormattedMessage {...messages.existing} />
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }} />
                                                        <FormattedMessage {...messages.new} />
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )
                                }
                            },
                            {
                                message: formatMessage(messages.manual),
                                to: `${basePath}/manual`,
                                route: {
                                    path: `${basePath}/manual`,
                                    exact: true,
                                    render: () => (
                                        <div className="space-y-3">
                                            <Paragraph className="text-sm text-gray-600 mb-3">
                                                <FormattedMessage {...messages.manualInstructions} />
                                            </Paragraph>
                                            {timeSlots.map((slot, index) => (
                                                <div
                                                    key={slot.uid || `new-${index}`}
                                                    className={`flex gap-3 items-start p-3 rounded-lg ${
                                                        showExistingIndicators && slot.isExisting
                                                            ? 'bg-blue-50 border border-blue-200'
                                                            : 'bg-gray-50'
                                                    }`}
                                                >
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                                            <FormattedMessage {...messages.startTime} />
                                                        </label>
                                                        <DateTime
                                                            value={slot.startDatetime ? moment(slot.startDatetime).toDate() : null}
                                                            inputProps={{ className: 'h-9 px-3 rounded border-gray-300 border w-full text-sm' }}
                                                            closeOnSelect
                                                            onChange={(value) => handleTimeSlotChange(index, 'startDatetime', moment(value).format('YYYY-MM-DDTHH:mm'))}
                                                        />
                                                    </div>
                                                    <span className="flex items-center text-gray-500 mt-6">
                                                        <FormattedMessage {...messages.to} />
                                                    </span>
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                                            <FormattedMessage {...messages.endTime} />
                                                        </label>
                                                        <DateTime
                                                            value={slot.endDatetime ? moment(slot.endDatetime).toDate() : null}
                                                            inputProps={{ className: 'h-9 px-3 rounded border-gray-300 border w-full text-sm' }}
                                                            closeOnSelect
                                                            onChange={(value) => handleTimeSlotChange(index, 'endDatetime', moment(value).format('YYYY-MM-DDTHH:mm'))}
                                                        />
                                                    </div>
                                                    {showExistingIndicators && slot.isExisting && (
                                                        <span className="mt-6 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded self-start">
                                                            <FormattedMessage {...messages.existing} />
                                                        </span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTimeSlot(index)}
                                                        className="mt-6 text-red-600 hover:text-red-800"
                                                        title={formatMessage(messages.removeTimeSlot)}
                                                    >
                                                        <Icon icon={['fas', 'trash']} />
                                                    </button>
                                                </div>
                                            ))}
                                            <Button type="button" onClick={addTimeSlot} variant="outline" size="sm">
                                                <FormattedMessage {...messages.addTimeSlot} />
                                            </Button>
                                        </div>
                                    )
                                }
                            }
                        ]}
                    />

                    {timeSlots.length > 0 && (
                        <div className="mt-4">
                            <Heading level={4} className="text-sm font-medium text-gray-700 mb-2">
                                <FormattedMessage {...messages.timeSlotsSummaryLabel} values={{ count: timeSlots.length }} />
                            </Heading>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {[...timeSlots]
                                    .sort((a, b) => moment(a.startDatetime).valueOf() - moment(b.startDatetime).valueOf())
                                    .map((slot) => {
                                        const originalIndex = timeSlots.indexOf(slot)
                                        const isNew = showExistingIndicators && !slot.isExisting
                                        return (
                                            <div
                                                key={slot.uid || `new-${originalIndex}`}
                                                className={`flex justify-between items-center p-2 rounded text-sm ${isNew ? 'bg-green-700' : 'bg-primary-lighter'}`}
                                            >
                                                <span className="text-white flex items-center gap-2">
                                                    <FormattedDate value={moment(slot.startDatetime).toDate()} />
                                                    {' '}
                                                    <FormattedTime value={moment(slot.startDatetime).toDate()} />
                                                    {' - '}
                                                    <FormattedTime value={moment(slot.endDatetime).toDate()} />
                                                    {isNew && (
                                                        <span className="px-1 py-0.5 bg-green-500 text-white text-xs rounded">
                                                            <FormattedMessage {...messages.new} />
                                                        </span>
                                                    )}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeTimeSlot(originalIndex)}
                                                    className="text-red-300 hover:text-red-100"
                                                    title={formatMessage(messages.remove)}
                                                >
                                                    <Icon icon={['fas', 'times']} />
                                                </button>
                                            </div>
                                        )
                                    })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex gap-3">
                    <Button type="submit" disabled={isSubmitting}>
                        {submitLabel}
                    </Button>
                    <Button type="button" onClick={onCancel} variant="outline">
                        <FormattedMessage {...messages.cancel} />
                    </Button>
                </div>
            </form>
        </Card>
    )
}

export default SchedulingPollForm
